import { useEffect, useState } from "react";
import { api, rupee } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Printer } from "lucide-react";

export default function POS() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState({ name: "Walk-in", phone: "" });
  const [payment, setPayment] = useState("cash");
  const [discount, setDiscount] = useState(0);
  const [bill, setBill] = useState(null);

  useEffect(() => { api.get(`/products?limit=200${q ? `&q=${q}` : ""}`).then((r) => setProducts(r.data)); }, [q, api]);

  const add = (p) => setCart((prev) => {
    const ex = prev.find((i) => i.product_id === p.id);
    if (ex) return prev.map((i) => i.product_id === p.id ? { ...i, quantity: i.quantity + 1 } : i);
    return [...prev, { product_id: p.id, name: p.name, price: p.discount_price || p.price, quantity: 1 }];
  });
  const remove = (id) => setCart((p) => p.filter((i) => i.product_id !== id));

  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const tax = Math.round((subtotal - discount) * 0.05);
  const total = subtotal - discount + tax;

  const checkout = async () => {
    if (cart.length === 0) return;
    try {
      const r = await api.post("/admin/pos/bill", { items: cart, subtotal, discount, tax, total, payment_method: payment, customer_name: customer.name, customer_phone: customer.phone });
      setBill({ ...r.data, cart, subtotal, tax, total, discount, customer, payment });
      setCart([]); toast.success("Bill created");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div>
      <h1 className="font-serif text-3xl mb-4">Billing / POS</h1>
      <div className="grid md:grid-cols-[1fr_400px] gap-6">
        <div>
          <input placeholder="Search product / SKU" value={q} onChange={(e) => setQ(e.target.value)} className="admin-input w-full mb-4" data-testid="pos-search-input" />
          <div className="grid grid-cols-3 gap-3 max-h-[70vh] overflow-y-auto">
            {products.map((p) => (
              <button key={p.id} onClick={() => add(p)} className="admin-surface p-3 text-left hover:border-[#C5A059]" data-testid={`pos-product-${p.sku}`}>
                <img src={p.images?.[0]} alt={p.name} className="w-full h-24 object-cover mb-2" />
                <p className="text-xs">{p.name}</p>
                <p className="font-mono text-[10px] text-[#8C867D]">{p.sku}</p>
                <p className="text-sm text-[#C5A059] mt-1">{rupee(p.discount_price || p.price)}</p>
              </button>
            ))}
          </div>
        </div>
        <aside className="admin-surface p-6 h-fit">
          <p className="eyebrow mb-4">Current Bill</p>
          <input placeholder="Customer name" value={customer.name} onChange={(e) => setCustomer({...customer, name: e.target.value})} className="admin-input w-full text-xs mb-2" data-testid="pos-customer-name" />
          <input placeholder="Phone" value={customer.phone} onChange={(e) => setCustomer({...customer, phone: e.target.value})} className="admin-input w-full text-xs mb-4" />
          <div className="space-y-2 max-h-60 overflow-y-auto text-sm">
            {cart.map((i) => (
              <div key={i.product_id} className="flex justify-between items-center">
                <span className="text-xs flex-1">{i.name} × {i.quantity}</span>
                <span>{rupee(i.price * i.quantity)}</span>
                <button onClick={() => remove(i.product_id)} className="ml-2"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
          <div className="border-t border-[#27272A] mt-4 pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{rupee(subtotal)}</span></div>
            <div className="flex justify-between items-center"><span>Discount</span><input type="number" value={discount} onChange={(e) => setDiscount(+e.target.value||0)} className="admin-input w-20 text-xs" data-testid="pos-discount" /></div>
            <div className="flex justify-between"><span>Tax</span><span>{rupee(tax)}</span></div>
            <div className="flex justify-between font-medium"><span>Total</span><span className="text-[#C5A059]">{rupee(total)}</span></div>
          </div>
          <select value={payment} onChange={(e) => setPayment(e.target.value)} className="admin-input w-full mt-4 text-xs" data-testid="pos-payment">
            {["cash","upi","card","gpay","phonepe","paytm"].map(p => <option key={p} value={p}>{p.toUpperCase()}</option>)}
          </select>
          <button onClick={checkout} disabled={cart.length === 0} className="bg-[#C5A059] text-[#121212] w-full py-2 mt-4 text-xs uppercase tracking-widest" data-testid="pos-checkout-button">Generate Invoice</button>
        </aside>
      </div>

      {bill && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setBill(null)}>
          <div className="bg-white text-black p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-center font-serif text-2xl tracking-widest">LEE BAG</div>
            <p className="text-center text-xs uppercase tracking-widest mt-1">Tax Invoice</p>
            <p className="font-mono text-xs mt-4">{bill.order_id}</p>
            <p className="text-xs">Customer: {bill.customer.name}</p>
            <div className="border-t border-b border-[#E2DDD5] py-3 mt-3 space-y-1 text-sm">
              {bill.cart.map((i) => <div key={i.product_id} className="flex justify-between"><span>{i.name} × {i.quantity}</span><span>{rupee(i.price * i.quantity)}</span></div>)}
            </div>
            <div className="text-sm space-y-1 mt-3">
              <div className="flex justify-between"><span>Subtotal</span><span>{rupee(bill.subtotal)}</span></div>
              <div className="flex justify-between"><span>Discount</span><span>{rupee(bill.discount)}</span></div>
              <div className="flex justify-between"><span>Tax</span><span>{rupee(bill.tax)}</span></div>
              <div className="flex justify-between font-medium border-t border-[#E2DDD5] pt-1"><span>Total</span><span>{rupee(bill.total)}</span></div>
              <div className="flex justify-between text-xs uppercase"><span>Paid via</span><span>{bill.payment}</span></div>
            </div>
            <button onClick={() => window.print()} className="mt-4 w-full bg-[#121212] text-white py-2 text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-testid="pos-print"><Printer size={14} /> Print</button>
            <button onClick={() => setBill(null)} className="mt-2 w-full text-xs uppercase tracking-widest text-[#8C867D]">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
